import React, { useEffect, useRef, useState } from 'react';
import { Field, Link, LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import SVG from 'helpers/SVG/SVG';
import { useLoginContext } from 'helpers/Providers/LoginContextProvider';
import AdditionalLinksModal from './AdditionalLinksModal';
import {
  OktaLoginProps,
  AdditionalLinksCollection,
  OktaSignInWidget,
  getOktaStatus,
  createOktaTokens,
  patchRequestBody,
  isOktaAuthResponse,
  buildOktaConfig,
  createForgotLink,
  createCapsLockWarning,
  createErrorObserver,
  setupPasswordToggleAccessibility,
  submitForm,
  createHandleAuthSuccess,
  hideOktaWidget,
} from './OktaLogin.helpers';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';

type ExtendedSitecoreContextProps = {
  FDICText?: Field<string>;
  FDICLogo?: { url: string; altText: string };
  IncludeFDICLogo?: Field<boolean>;
  route?: {
    fields?: {
      DiscludeFDIC?: Field<boolean>;
    };
  };
};

const OktaLogin: React.FC<OktaLoginProps> = (props) => {
  const { isMarqueeLoginEnabled } = useLoginContext();
  const onError = React.useMemo(() => props.onError ?? (() => {}), [props.onError]);
  const widgetId = 'okta-signin-widget';
  const signInRef = useRef<OktaSignInWidget | null>(null);
  const isMountedRef = useRef(true);
  const loginDialogRef = useRef<HTMLDivElement>(null);
  const didSubmitRef = useRef(false);
  // Track if the login dialog was open when we launched the additional links modal
  const wasDialogOpenRef = useRef(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Track latest showModal value for event handlers without re-binding
  const showModalRef = useRef(showModal);
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  // Extract FDIC configuration from Sitecore context
  const extendedSitecoreContext: ExtendedSitecoreContextProps =
    useSitecoreContext().sitecoreContext;

  const includeFDICLogo = extendedSitecoreContext.IncludeFDICLogo?.value;
  const discludeFDIC = extendedSitecoreContext.route?.fields?.['DiscludeFDIC'] as Field<boolean>;
  const FDICImage = extendedSitecoreContext.FDICLogo?.url;
  const FDICImageAltText = extendedSitecoreContext.FDICLogo?.altText;
  const FDICText = extendedSitecoreContext.FDICText?.value;

  const loginLinks = props?.fields?.children?.filter(
    (item) => item.templateId === 'f2693d7b-0b38-42ab-8269-2cf717b708e9'
  )[0];
  const additionalLinks = props?.fields?.children?.filter(
    (item) => item.templateId === '51da10f9-6b4a-4c1b-b7a4-18f71f999447'
  )[0] as AdditionalLinksCollection;

  const baseHandleAuthSuccess = React.useMemo(
    () => createHandleAuthSuccess(props, true, widgetId),
    [props]
  );
  const handleAuthSuccess = React.useCallback(
    (result: unknown) => {
      if (didSubmitRef.current) return;
      baseHandleAuthSuccess(result);
      didSubmitRef.current = true;
    },
    [baseHandleAuthSuccess]
  );

  const handleEscape = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDialog) setShowDialog(false);
    },
    [showDialog]
  );

  useEffect(() => {
    if (showDialog) {
      document.addEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = '';
    };
  }, [showDialog, handleEscape]);

  useEffect(() => {
    if (showDialog && loginDialogRef.current) {
      const loginElements = loginDialogRef.current.querySelectorAll(
        'a[href], button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (loginElements.length > 0) {
        const first = loginElements[0] as HTMLElement;
        const last = loginElements[loginElements.length - 1] as HTMLElement;
        first.focus();
        const catchTab = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        };
        document.addEventListener('keydown', catchTab);
        return () => document.removeEventListener('keydown', catchTab);
      }
    }
    return undefined;
  }, [showDialog]);

  // Patch fetch to capture auth responses
  useEffect(() => {
    if (typeof window === 'undefined' || !('fetch' in window)) return;
    const originalFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      if (args[1] && typeof args[1].body === 'string') {
        args[1].body = patchRequestBody(args[1].body);
      }
      const response = await originalFetch.apply(window, args);
      const eisDomain = props?.fields?.EisLink?.value?.href || '';
      if (isOktaAuthResponse(response, eisDomain)) {
        const data = await response.clone().json();
        const oktaStatus = getOktaStatus(data.status);
        const tokens = createOktaTokens(data, oktaStatus);
        if (!didSubmitRef.current) {
          hideOktaWidget(widgetId);
          submitForm(tokens, props?.fields?.SubmitLink?.value?.href || '');
          didSubmitRef.current = true;
        }
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, [props?.fields?.EisLink?.value?.href, props?.fields?.SubmitLink?.value?.href]);

  // Initial widget load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    isMountedRef.current = true;
    const load = async () => {
      try {
        const clientId = process.env.OKTA_LOGIN_CLIENT_ID;
        if (!clientId) {
          console.warn('OKTA_LOGIN_CLIENT_ID is not defined');
        }
        const { default: OktaSignIn } = await import('@okta/okta-signin-widget');
        if (!isMountedRef.current) return;
        if (signInRef.current) {
          signInRef.current.remove();
          signInRef.current = null;
        }
        const config = buildOktaConfig(props);
        // @ts-expect-error issuer intentionally omitted
        const signIn = new OktaSignIn(config);
        signInRef.current = signIn;
        signIn.renderEl({ el: `#${widgetId}` }, handleAuthSuccess, (err) => onError(err));
        const forgotLinkHref = props?.fields?.ForgotUsernameLink?.value?.href || '#';
        const errorText =
          props?.fields?.InvalidCredentials?.value ||
          'Invalid username or password. Please try again.';
        setTimeout(createForgotLink(widgetId, forgotLinkHref), 150);
        setTimeout(createCapsLockWarning(widgetId), 200);
        setTimeout(createErrorObserver(widgetId, errorText), 250);
        setTimeout(setupPasswordToggleAccessibility(widgetId), 300);
      } catch (e) {
        onError(e);
      }
    };
    load();
    return () => {
      isMountedRef.current = false;
      if (signInRef.current) {
        signInRef.current.remove();
        signInRef.current = null;
      }
    };
  }, [
    handleAuthSuccess,
    onError,
    props?.fields?.ForgotUsernameLink?.value?.href,
    props?.fields?.InvalidCredentials?.value,
    props,
  ]);

  // Re-render widget when dialog toggles (to ensure fresh DOM)
  useEffect(() => {
    if (!signInRef.current) return;
    const rerender = async () => {
      try {
        signInRef.current?.remove();
        await new Promise((r) => setTimeout(r, 50));
        signInRef.current?.renderEl({ el: `#${widgetId}` }, handleAuthSuccess, (err) =>
          onError(err)
        );
        const forgotLinkHref = props?.fields?.ForgotUsernameLink?.value?.href || '#';
        const errorText =
          props?.fields?.InvalidCredentials?.value ||
          'Invalid username or password. Please try again.';
        setTimeout(createForgotLink(widgetId, forgotLinkHref), 150);
        setTimeout(createCapsLockWarning(widgetId), 200);
        setTimeout(createErrorObserver(widgetId, errorText), 250);
        setTimeout(setupPasswordToggleAccessibility(widgetId), 300);
      } catch (e) {
        console.error('Error re-rendering widget:', e);
      }
    };
    rerender();
  }, [
    showDialog,
    handleAuthSuccess,
    onError,
    props?.fields?.ForgotUsernameLink?.value?.href,
    props?.fields?.InvalidCredentials?.value,
    widgetId,
    props,
  ]);

  // Style overrides
  useEffect(() => {
    const style = document.createElement('style');
    const safeTitle = (props?.fields?.Title?.value || 'BOKF').replace(/'/g, "\\'");
    style.innerHTML = `#${widgetId} .auth-org-logo{display:none!important;} #${widgetId} .okta-sign-in-header::before{content:'${safeTitle}';}`;
    document.head.appendChild(style);
    return () => style.remove();
  }, [props?.fields?.Title?.value, widgetId]);

  // Nav login click listeners
  useEffect(() => {
    const navLogins = Array.from(document.getElementsByClassName('nav-login'));
    const handleClick = (e: Event) => {
      e.preventDefault();
      // If the additional links modal is open, ignore attempts to open login
      if (showModalRef.current) return;
      setShowDialog(true);
    };
    for (const nl of navLogins) {
      nl.addEventListener('click', handleClick);
    }
    return () => {
      for (const nl of navLogins) {
        nl.removeEventListener('click', handleClick);
      }
    };
  }, []);

  // Ensure only one dialog (login vs additional links) is visible at a time
  useEffect(() => {
    if (showModal && showDialog) {
      // Prefer showing the additional links modal; hide login
      setShowDialog(false);
    }
  }, [showModal, showDialog]);

  // When the additional links modal closes, restore the login dialog if it was previously open
  useEffect(() => {
    if (!showModal && wasDialogOpenRef.current && !showDialog) {
      setShowDialog(true);
      wasDialogOpenRef.current = false;
    }
  }, [showModal, showDialog]);

  return (
    <>
      {isMarqueeLoginEnabled && (
        <div
          data-component="login"
          className={`flex flex-col h-full ${showDialog ? 'hidden' : ''}`}
        >
          <div className="okta-sign-in-container-marquee flex grow">
            {!showDialog && <div id={widgetId} />}
          </div>
          <div className="w-[400px] min-w-[300px] text-center px-6 py-4 bg-light-background-neutral-low grid grid-rows-1 gap-y-3 mx-auto">
            <button
              className="flex items-center justify-center gap-x-1 text-light-fill-secondary c1-medium"
              onClick={() => {
                // Record prior dialog state and toggle visibility appropriately
                wasDialogOpenRef.current = showDialog;
                setShowModal(true);
                if (showDialog) setShowDialog(false);
              }}
            >
              <div>{additionalLinks?.fields?.['Call Modal Text']?.value}</div>
              <SVG svg="icon-user-group-female-male" />
            </button>
            <div className="c1 text-light-fill-primary-variant">
              Enroll in{' '}
              <Link
                field={loginLinks?.fields.children[0].fields?.Link as LinkField}
                className={`text-light-fill-primary-variant font-normal underline ${loginLinks?.fields.children[0].fields?.Link?.value.class}`}
              />{' '}
              or{' '}
              <Link
                field={loginLinks?.fields.children[1].fields?.Link as LinkField}
                className={`text-light-fill-primary-variant font-normal underline ${loginLinks?.fields.children[1].fields?.Link?.value.class}`}
              />
            </div>
          </div>
        </div>
      )}
      {showDialog && (
        <dialog
          className="fixed w-full h-full inset-0 overflow-y-auto z-50 bg-scrim-clickable"
          open
          aria-modal="true"
          role="dialog"
        >
          <div className="fixed w-full h-full" onClick={() => setShowDialog(false)}></div>
          <div className="min-h-screen" ref={loginDialogRef}>
            <div className="absolute top-0 sm:top-1/4 lg:top-[10%] left-0 right-0 mx-auto w-full max-w-[350px] bg-light-background-neutral-variant overflow-auto bok-scrollbar bok-scrollbar--no-border">
              <button
                className="absolute right-0 top-0 m-4"
                aria-label="Close login dialog"
                onClick={() => {
                  // Fully close any open dialogs (login + additional links)
                  wasDialogOpenRef.current = false;
                  setShowModal(false);
                  setShowDialog(false);
                }}
                tabIndex={0}
              >
                <SVG
                  className="[&_svg]:w-4 [&_svg]:h-4 [&_path]:fill-light-fill-primary-variant"
                  svg="icon-close"
                />
              </button>
              <div data-component="login" className="">
                <div className="okta-sign-in-container-dialog pt-16">
                  {showDialog && <div id={widgetId} />}
                </div>
                <div className="additional-links-container-dialog text-center px-6 py-4 bg-light-background-neutral-low grid grid-rows-1 gap-y-3 mt-4">
                  <button
                    className="flex items-center justify-center gap-x-1 text-light-fill-secondary c1-medium"
                    onClick={() => {
                      wasDialogOpenRef.current = showDialog;
                      setShowModal(true);
                      if (showDialog) setShowDialog(false);
                    }}
                  >
                    <div>{additionalLinks?.fields?.['Call Modal Text']?.value}</div>
                    <SVG svg="icon-user-group-female-male" />
                  </button>
                  <div className="c1 text-light-fill-primary-variant">
                    Enroll in{' '}
                    <Link
                      field={loginLinks?.fields.children[0].fields?.Link as LinkField}
                      className={`text-light-fill-primary-variant font-normal underline ${loginLinks?.fields.children[0].fields?.Link?.value.class}`}
                    />{' '}
                    or{' '}
                    <Link
                      field={loginLinks?.fields.children[1].fields?.Link as LinkField}
                      className={`text-light-fill-primary-variant font-normal underline ${loginLinks?.fields.children[1].fields?.Link?.value.class}`}
                    />
                  </div>
                </div>
                {/* FDIC */}
                {includeFDICLogo && !discludeFDIC?.value && (
                  <div className="bg-light-background-neutral-variant px-6 py-3 text-[13px] flex items-center justify-center gap-x-2 shadow-small">
                    <ImageWrapper
                      className="w-full h-full min-w-10 max-w-fit"
                      field={{ value: { src: FDICImage, alt: FDICImageAltText } }}
                      height="17"
                      width="41"
                    />
                    <p className="italic">{FDICText}</p>
                  </div>
                )}
                {/* END FDIC */}
              </div>
            </div>
          </div>
        </dialog>
      )}
      <AdditionalLinksModal
        showModal={showModal}
        setShowModal={setShowModal}
        additionalLinks={additionalLinks}
        onGoBack={() => {
          // Reopen login in same render pass as closing links to avoid flicker
          setShowDialog(true);
          setShowModal(false);
          wasDialogOpenRef.current = true;
        }}
        onCloseAll={() => {
          // Fully close both the additional links and login dialogs
          wasDialogOpenRef.current = false;
          setShowModal(false);
          setShowDialog(false);
        }}
      />
    </>
  );
};

export default OktaLogin;
